'use client';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Zap, RefreshCw, Loader2 } from 'lucide-react';
import SignalCard from './SignalCard';
import SignalEmptyState from './SignalEmptyState';
import PaywallModal from './PaywallModal';
import type { SignalData } from '../utils/signalGenerator';
import { useLanguage } from '@/contexts/LanguageContext';
import SetSignalAlert from './SetSignalAlert';

// Map a raw Supabase signal row to the SignalData shape expected by SignalCard
function mapDbSignalToSignalData(row: Record<string, any>): SignalData {
  const direction: 'BUY' | 'SELL' = row.direction === 'SELL' ? 'SELL' : 'BUY';
  const isBuy = direction === 'BUY';

  const fmt = (v: any, digits = 2) =>
    v != null ? Number(v).toFixed(digits) : 'N/A';

  const bestLow = row.entry_best_low != null ? Number(row.entry_best_low) : 0;
  const bestHigh = row.entry_best_high != null ? Number(row.entry_best_high) : 0;
  const okLow = row.entry_ok_low != null ? Number(row.entry_ok_low) : 0;
  const okHigh = row.entry_ok_high != null ? Number(row.entry_ok_high) : 0;

  const tradeSL = row.trade_sl != null ? Number(row.trade_sl) : 0;
  const hardSL = row.hard_sl != null ? Number(row.hard_sl) : 0;
  const currentPrice = row.current_price != null ? Number(row.current_price) : 0;

  const tpKeys = ['tp1', 'tp2', 'tp3', 'tp4', 'tp5'] as const;
  const tpLevels = tpKeys
    .map((key, i) => {
      const val = row[key];
      if (val == null) return null;
      const price = Number(val);
      const pips = Math.abs(Math.round((price - currentPrice) * 10));
      const slPips = Math.abs(Math.round((tradeSL - currentPrice) * 10)) || 1;
      const prob = row[`tp${i + 1}_probability`] ?? Math.max(5, 80 - i * 15);
      const percentKey = `tp${i + 1}_percent`;
      const percent = row[percentKey] != null ? Number(row[percentKey]) : null;
      return {
        level: i + 1,
        price: fmt(price),
        pips,
        probability: Number(prob),
        rr: `1:${(pips / slPips).toFixed(1)}`,
        percent,
      };
    })
    .filter(Boolean) as SignalData['tpLevels'];

  const base = row.signal_time ? new Date(row.signal_time) : new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const addMin = (d: Date, m: number) => {
    const nd = new Date(d.getTime() + m * 60000);
    return `${pad(nd.getUTCHours())}:${pad(nd.getUTCMinutes())}`;
  };

  const aiScore = row.overall_setup_score ?? 0;
  const confidenceScore = row.direction_confidence ?? 0;

  const signalId = `XAU-${String(row.id || '').slice(-4).toUpperCase() || '0000'}`;
  const createdAt = new Date(row.created_at || Date.now());
  const genTime = `${pad(createdAt.getUTCHours())}:${pad(createdAt.getUTCMinutes())} UTC`;

  return {
    id: signalId,
    direction,
    currentPrice: currentPrice > 0 ? fmt(currentPrice) : 'N/A',
    entryZone: {
      best: {
        low: fmt(Math.min(bestLow, bestHigh)),
        high: fmt(Math.max(bestLow, bestHigh)),
        spread: Math.round(Math.abs(bestHigh - bestLow) * 10),
      },
      ok: {
        low: fmt(Math.min(okLow, okHigh)),
        high: fmt(Math.max(okLow, okHigh)),
        spread: Math.round(Math.abs(okHigh - okLow) * 10),
      },
    },
    noChasePrice: row.no_chase != null ? fmt(row.no_chase) : (isBuy ? fmt(bestHigh + 2) : fmt(bestLow - 2)),
    tpLevels: tpLevels.length > 0 ? tpLevels : [
      { level: 1, price: 'N/A', pips: 0, probability: 0, rr: '1:0', percent: null },
    ],
    tradeSL: fmt(tradeSL),
    tradeSLPips: Math.abs(Math.round((tradeSL - currentPrice) * 10)),
    hardSL: fmt(hardSL),
    hardSLPips: Math.abs(Math.round((hardSL - currentPrice) * 10)),
    confidenceScore,
    aiScore,
    setupQuality: aiScore >= 85 ? 'A+' : aiScore >= 70 ? 'A' : aiScore >= 55 ? 'B' : 'C',
    tradeMode: row.mode || 'STANDARD',
    timing: {
      bestEntryTime: addMin(base, 1),
      enterByTime: addMin(base, 10),
      validUntilTime: addMin(base, 19),
    },
    contextNote: `${direction} signal — Entry: ${fmt(Math.min(bestLow, bestHigh))}–${fmt(Math.max(bestLow, bestHigh))} · Score: ${aiScore}/100`,
    rrRatio: tpLevels[2]?.rr?.replace('1:', '') || '0',
    marketStructure: isBuy ? 'Bullish HH/HL' : 'Bearish LH/LL',
    generatedAt: genTime,
    signalStatus: (() => {
      const raw = (row.signal_status || '').toString().toUpperCase();
      if (raw === 'READY') return 'READY';
      if (raw === 'NO_TRADE' || raw === 'NO TRADE') return 'NO_TRADE';
      if (raw === 'WATCH') return 'WATCH';
      if (aiScore >= 75) return 'READY';
      if (aiScore >= 50) return 'WATCH';
      return 'NO_TRADE';
    })(),
    tp1Percent: row.tp1_percent != null ? Number(row.tp1_percent) : null,
    tp2Percent: row.tp2_percent != null ? Number(row.tp2_percent) : null,
  };
}

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 120000; // 2 minutes
const RATE_LIMIT_SECONDS = 60; // cooldown between requests
const FREE_LIMIT = 5;

const STORAGE_KEY = 'xau_signal_usage';
const WINDOW_MS = 24 * 60 * 60 * 1000;

// Returns true when running inside the Rocket admin/preview environment
function isRocketPreview(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.hostname.includes('builtwithrocket.new');
}

// Generate or retrieve a persistent anonymous device ID
function getDeviceId(): string {
  if (typeof window === 'undefined') return 'ssr';
  let id = localStorage.getItem('xau_device_id');
  if (!id) {
    id = `anon_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem('xau_device_id', id);
  }
  return id;
}

interface LocalUsage {
  timestamps: number[];
}

function getLocalUsage(): { signals_used: number; signals_remaining: number; reset_at: string | null } {
  if (typeof window === 'undefined') return { signals_used: 0, signals_remaining: FREE_LIMIT, reset_at: null };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const data: LocalUsage = raw ? JSON.parse(raw) : { timestamps: [] };
    const now = Date.now();
    const recent = (data.timestamps || []).filter((t) => now - t < WINDOW_MS);
    const signals_used = recent.length;
    const signals_remaining = Math.max(0, FREE_LIMIT - signals_used);
    const oldest = recent.length > 0 ? Math.min(...recent) : null;
    const reset_at = oldest ? new Date(oldest + WINDOW_MS).toISOString() : null;
    // Persist cleaned list
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ timestamps: recent }));
    return { signals_used, signals_remaining, reset_at };
  } catch {
    return { signals_used: 0, signals_remaining: FREE_LIMIT, reset_at: null };
  }
}

function incrementLocalUsage(): { signals_used: number; signals_remaining: number; reset_at: string } {
  if (typeof window === 'undefined') return { signals_used: 1, signals_remaining: FREE_LIMIT - 1, reset_at: new Date(Date.now() + WINDOW_MS).toISOString() };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const data: LocalUsage = raw ? JSON.parse(raw) : { timestamps: [] };
    const now = Date.now();
    const recent = (data.timestamps || []).filter((t) => now - t < WINDOW_MS);
    recent.push(now);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ timestamps: recent }));
    const signals_used = recent.length;
    const signals_remaining = Math.max(0, FREE_LIMIT - signals_used);
    const oldest = Math.min(...recent);
    const reset_at = new Date(oldest + WINDOW_MS).toISOString();
    return { signals_used, signals_remaining, reset_at };
  } catch {
    return { signals_used: 1, signals_remaining: FREE_LIMIT - 1, reset_at: new Date(Date.now() + WINDOW_MS).toISOString() };
  }
}

export default function SignalGeneratorSection() {
  const [signal, setSignal] = useState<SignalData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [showPaywall, setShowPaywall] = useState(false);
  const [signalsUsed, setSignalsUsed] = useState(0);
  const [signalsRemaining, setSignalsRemaining] = useState<number>(FREE_LIMIT);
  const [resetAt, setResetAt] = useState<Date | null>(null);
  const [countdownDisplay, setCountdownDisplay] = useState<string>('');
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollStartRef = useRef<number>(0);
  const cooldownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { t } = useLanguage();

  // Format remaining time as "Xh Ym"
  const formatCountdown = (targetDate: Date): string => {
    const now = Date.now();
    const diff = targetDate.getTime() - now;
    if (diff <= 0) return '';
    const totalSeconds = Math.floor(diff / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds}s`;
  };

  // Start countdown interval when resetAt is set
  useEffect(() => {
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    if (!resetAt) {
      setCountdownDisplay('');
      return;
    }
    const display = formatCountdown(resetAt);
    setCountdownDisplay(display);
    if (!display) return;

    countdownTimerRef.current = setInterval(() => {
      const remaining = formatCountdown(resetAt);
      setCountdownDisplay(remaining);
      if (!remaining) {
        if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
        setResetAt(null);
        setSignalsUsed(0);
        setSignalsRemaining(FREE_LIMIT);
      }
    }, 1000);

    return () => {
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    };
  }, [resetAt]);

  // Load usage from localStorage on mount
  const fetchUsage = useCallback(() => {
    // In Rocket preview environment, always show unlimited usage
    if (isRocketPreview()) {
      setSignalsUsed(0);
      setSignalsRemaining(FREE_LIMIT);
      setResetAt(null);
      return;
    }
    const local = getLocalUsage();
    setSignalsUsed(local.signals_used);
    setSignalsRemaining(local.signals_remaining);
    if (local.reset_at && local.signals_remaining === 0) {
      setResetAt(new Date(local.reset_at));
    }
  }, []);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    };
  }, []);

  const startCooldown = () => {
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    setCooldownSeconds(RATE_LIMIT_SECONDS);
    cooldownTimerRef.current = setInterval(() => {
      setCooldownSeconds((prev) => {
        if (prev <= 1) {
          if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const pollForResult = async (requestId: string) => {
    const elapsed = Date.now() - pollStartRef.current;

    if (elapsed >= POLL_TIMEOUT_MS) {
      stopPolling();
      setIsLoading(false);
      setError('Signal generation timed out. Make sure your background script is running.');
      setHasGenerated(true);
      return;
    }

    try {
      const res = await fetch(`/api/signal-requests?id=${requestId}`);
      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.error || 'Failed to poll signal request');
      }

      const { request, signal: signalRow } = json;

      if (request.status === 'completed' && signalRow) {
        stopPolling();
        const mapped = mapDbSignalToSignalData(signalRow);
        setSignal(mapped);
        setHasGenerated(true);
        setIsLoading(false);
        setStatusMessage('');
        toast.success(
          `${mapped.direction} signal ready — Score ${mapped.aiScore}`,
          {
            description: `Entry zone: ${mapped.entryZone.best.low} – ${mapped.entryZone.best.high}`,
            duration: 4000,
          }
        );
        fetch('/api/signal-alerts/check', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ signal: signalRow }),
        }).catch((err) => console.error('Alert check failed:', err));
      } else if (request.status === 'error') {
        stopPolling();
        setIsLoading(false);
        setError(request.error_message || 'Signal generation failed on the background script.');
        setHasGenerated(true);
      } else {
        const secondsWaited = Math.floor(elapsed / 1000);
        setStatusMessage(`Waiting for signal… ${secondsWaited}s`);
        pollTimerRef.current = setTimeout(() => pollForResult(requestId), POLL_INTERVAL_MS);
      }
    } catch (err: any) {
      stopPolling();
      setIsLoading(false);
      setError(err.message || 'Failed to poll for signal result');
      setHasGenerated(true);
    }
  };

  const requestSignal = async () => {
    if (cooldownSeconds > 0) return;

    // Client-side guard for free users — skip in Rocket preview environment
    if (!isRocketPreview() && signalsRemaining === 0) {
      setShowPaywall(true);
      return;
    }

    stopPolling();
    setIsLoading(true);
    setSignal(null);
    setError(null);
    setStatusMessage('Sending signal request…');
    startCooldown();

    try {
      const deviceId = getDeviceId();
      const res = await fetch('/api/signal-requests', {
        method: 'POST',
        headers: { 'x-device-id': deviceId },
      });
      const json = await res.json();

      if (res.status === 429 && json.error === 'limit_reached') {
        setIsLoading(false);
        setSignalsUsed(json.signals_used ?? FREE_LIMIT);
        setSignalsRemaining(0);
        if (json.reset_at) {
          setResetAt(new Date(json.reset_at));
        }
        setShowPaywall(true);
        return;
      }

      if (!res.ok) {
        throw new Error(json.error || 'Failed to create signal request');
      }

      // Update usage — skip localStorage increment in Rocket preview environment
      if (!isRocketPreview()) {
        const local = incrementLocalUsage();
        setSignalsUsed(local.signals_used);
        setSignalsRemaining(local.signals_remaining);
        if (local.signals_remaining === 0) {
          setResetAt(new Date(local.reset_at));
        }
      }

      const requestId = json.request?.id;
      if (!requestId) throw new Error('No request ID returned');

      setStatusMessage('Request sent — waiting for background script…');
      pollStartRef.current = Date.now();
      pollTimerRef.current = setTimeout(() => pollForResult(requestId), POLL_INTERVAL_MS);
    } catch (err: any) {
      setIsLoading(false);
      setError(err.message || 'Failed to request signal');
      setHasGenerated(true);
      toast.error('Failed to request signal', { duration: 4000 });
    }
  };

  const isLimitReached = signalsRemaining === 0;

  return (
    <section className="space-y-3.5">
      {/* Paywall Modal */}
      <PaywallModal
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        signalsUsed={signalsUsed}
        signalsLimit={FREE_LIMIT}
      />

      {/* Section header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-zinc-100 tracking-tight">
            {t('signalGenerator')}
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            {t('signalGeneratorSub')}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Signal usage counter */}
          <div
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium ${
              isLimitReached
                ? 'bg-red-500/10 border-red-500/30 text-red-400'
                : signalsRemaining <= 2
                ? 'bg-amber-500/10 border-amber-500/20 text-amber-400' :'bg-zinc-900 border-white/5 text-zinc-400'
            }`}
          >
            <Zap className="w-3 h-3" />
            <span>
              {isLimitReached
                ? `${signalsUsed}/${FREE_LIMIT} — Limit reached`
                : `${signalsUsed}/${FREE_LIMIT} signals used today`}
            </span>
          </div>

          {hasGenerated && signal && !isLimitReached && (
            <button
              onClick={requestSignal}
              disabled={isLoading || cooldownSeconds > 0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-900 border border-white/5 hover:bg-zinc-800 hover:border-white/10 text-zinc-400 hover:text-zinc-200 text-xs font-medium transition-all duration-150 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} />
              {cooldownSeconds > 0 ? `Wait ${cooldownSeconds}s` : t('refreshSignal')}
            </button>
          )}
        </div>
      </div>

      {/* Free limit countdown banner */}
      {isLimitReached && countdownDisplay && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/5 px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-2 h-2 rounded-full bg-red-400 animate-pulse flex-shrink-0" />
            <p className="text-sm text-red-300 font-medium">
              Free limit reached. You can generate new signals in{' '}
              <span className="font-mono text-red-200">{countdownDisplay}</span>
            </p>
          </div>
          <button
            onClick={() => setShowPaywall(true)}
            className="flex-shrink-0 text-xs font-semibold text-violet-400 hover:text-violet-300 underline underline-offset-2 transition-colors"
          >
            Upgrade
          </button>
        </div>
      )}

      {/* Main CTA or Signal Card */}
      {!hasGenerated && !isLoading && (
        <SignalEmptyState onGetSignal={requestSignal} />
      )}

      {isLoading && <SignalLoadingSkeleton statusMessage={statusMessage} />}

      {error && !isLoading && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-5 text-center">
          <p className="text-sm text-amber-400 font-mono">{error}</p>
          <p className="text-xs text-zinc-500 mt-1">
            {t('signalRequestError')}
          </p>
        </div>
      )}

      {signal && !isLoading && (
        <SignalCard signal={signal} />
      )}

      {/* Get Signal Button — shown when no signal yet */}
      {!hasGenerated && !isLoading && (
        <div className="flex justify-center pt-1">
          <button
            onClick={isLimitReached ? () => setShowPaywall(true) : requestSignal}
            disabled={!isLimitReached && cooldownSeconds > 0}
            className="group relative flex items-center gap-2.5 px-6 py-3 rounded-xl font-semibold text-sm text-zinc-950 transition-all duration-200 active:scale-95 hover:shadow-[0_0_32px_rgba(212,175,55,0.25)] overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
            style={{
              background: isLimitReached
                ? 'linear-gradient(135deg, #6d28d9 0%, #a855f7 50%, #5b21b6 100%)'
                : 'linear-gradient(135deg, #D4AF37 0%, #F5D76E 50%, #B8961F 100%)',
            }}
          >
            <Zap className="w-4 h-4 transition-transform duration-150 group-hover:scale-110" />
            <span className="tracking-wide">
              {isLimitReached
                ? 'Upgrade to Continue'
                : cooldownSeconds > 0
                ? `Wait ${cooldownSeconds}s`
                : t('getSignal')}
            </span>
            <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors duration-150 rounded-xl" />
          </button>
        </div>
      )}

      {signal && !isLoading && (
        <div className="flex justify-center pt-0.5">
          <button
            onClick={isLimitReached ? () => setShowPaywall(true) : requestSignal}
            disabled={!isLimitReached && cooldownSeconds > 0}
            className="group relative flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-xs text-zinc-950 transition-all duration-200 active:scale-95 hover:shadow-[0_0_24px_rgba(212,175,55,0.2)] overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
            style={{
              background: isLimitReached
                ? 'linear-gradient(135deg, #6d28d9 0%, #a855f7 50%, #5b21b6 100%)'
                : 'linear-gradient(135deg, #D4AF37 0%, #F5D76E 50%, #B8961F 100%)',
            }}
          >
            <Zap className="w-3.5 h-3.5 transition-transform duration-150 group-hover:scale-110" />
            <span className="tracking-wide">
              {isLimitReached
                ? 'Upgrade to Continue'
                : cooldownSeconds > 0
                ? `Wait ${cooldownSeconds}s`
                : t('getNewSignal')}
            </span>
            <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors duration-150 rounded-xl" />
          </button>
        </div>
      )}

      {error && !isLoading && hasGenerated && (
        <div className="flex justify-center pt-1">
          <button
            onClick={requestSignal}
            disabled={cooldownSeconds > 0}
            className="group relative flex items-center gap-2.5 px-6 py-3 rounded-xl font-semibold text-sm text-zinc-950 transition-all duration-200 active:scale-95 overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: 'linear-gradient(135deg, #D4AF37 0%, #F5D76E 50%, #B8961F 100%)',
            }}
          >
            <Zap className="w-4 h-4" />
            <span className="tracking-wide">
              {cooldownSeconds > 0 ? `Wait ${cooldownSeconds}s` : t('tryAgain')}
            </span>
          </button>
        </div>
      )}

      {/* Set Signal Alert — collapsible section below signal generator */}
      <SetSignalAlert />
    </section>
  );
}

function SignalLoadingSkeleton({ statusMessage }: { statusMessage?: string }) {
  return (
    <div className="rounded-xl border border-white/5 bg-zinc-900/60 p-4 space-y-3.5 animate-pulse">
      {statusMessage && (
        <div className="flex items-center gap-2 justify-center pb-1">
          <Loader2 className="w-3.5 h-3.5 text-amber-400 animate-spin" />
          <span className="text-xs text-amber-400 font-mono">{statusMessage}</span>
        </div>
      )}
      <div className="flex items-start justify-between">
        <div className="space-y-1.5">
          <div className="h-7 w-24 bg-zinc-800 rounded-lg" />
          <div className="h-3.5 w-40 bg-zinc-800 rounded" />
        </div>
        <div className="flex gap-2">
          <div className="h-7 w-16 bg-zinc-800 rounded-lg" />
          <div className="h-7 w-20 bg-zinc-800 rounded-lg" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {['score-1', 'score-2', 'score-3'].map((k) => (
          <div key={k} className="h-20 bg-zinc-800 rounded-lg" />
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div className="h-28 bg-zinc-800 rounded-lg" />
        <div className="h-28 bg-zinc-800 rounded-lg" />
      </div>
      <div className="space-y-1.5">
        <div className="h-3.5 w-28 bg-zinc-800 rounded" />
        {['tp-1', 'tp-2', 'tp-3', 'tp-4', 'tp-5'].map((k) => (
          <div key={k} className="h-9 bg-zinc-800 rounded-lg" />
        ))}
      </div>
    </div>
  );
}